import { z } from 'zod';

class ProductStockNotification extends HTMLElement {
  constructor() {
    super();

    this.requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2024-05-15',
      },
    };

    this.submitButton = this.querySelector('[data-notify-submit]');

    this.emailSchema = z.string().email();
    this.emailInput = this.querySelector('input[type="email"]');
    this.emailValue;
    this.emailConsent;

    this.variantId = this.dataset.variantId;

    this.successElement = this.querySelector('[data-notify-success]');
  }

  connectedCallback() {
    this.submitButton.onclick = async (event) => this.handleSubmit(event);

    // Listen to the variant change event
    document.addEventListener('variant-change', this.handleAfterVariantChange.bind(this));
  }

  /**
   * Called when the variant changes.
   * @param {Object} event.detail The event detail.
   */
  handleAfterVariantChange({ detail }) {
    const id = detail?.variant.id;
    if (!id) return;
    this.variantId = id;
  }

  /**
   * Toggles the loading state of the submit button.
   * @param {Boolean} loading Whether the button is loading.
   * @returns {Boolean} Whether the button is loading.
   */
  isLoading(loading) {
    this.submitButton.classList.toggle('loading', loading);
    this.submitButton.disabled = loading;
    return loading;
  }

  /**
   * Handles the form submission.
   * @param {Event} event The form submission event.
   */
  async handleSubmit(event) {
    event.preventDefault();

    this.isLoading(true);

    this.emailValue = this.emailInput.value;

    try {
      // Validate email and return if invalid
      await this.emailSchema.parseAsync(this.emailValue);

      await this.handleBackInStockSubscription();
      await this.handleSubscription();

      this.emailValue = '';
      this.emailInput.value = '';

      this.showSuccessMessage();
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading(false);
    }
  }

  /**
   * Handles the subscription.
   * @tutorial https://developers.klaviyo.com/en/reference/create_client_subscription
   */
  async handleSubscription() {
    // If either the variantId or email is missing, return
    this.emailConsent = this.querySelector('input[type="checkbox"]')?.checked;

    if (!this.emailConsent) return;

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Back in Stock',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: this.emailValue,
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: window.klaviyo.listId,
            },
          },
        },
      },
    };

    this.requestOptions.body = JSON.stringify(payload);

    await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${window.klaviyo.companyId}`,
      this.requestOptions,
    );
  }

  /**
   * Handles the back-in-stock subscription.
   * @tutorial https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
   */
  async handleBackInStockSubscription() {
    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: this.emailValue,
              },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${this.variantId}`,
            },
          },
        },
      },
    };

    this.requestOptions.body = JSON.stringify(payload);

    const response = await fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${window.klaviyo.companyId}`,
      this.requestOptions,
    );

    // If response is ok and status is 202, return. The response to JSON will be empty and the transforming will fail.
    if (response.ok && response.status === 202) return;

    const data = await response.json();

    // If data.errors?.length, throw an array of errors
    if (data.errors?.length) throw new Error(data.errors[0].title);
  }

  /**
   * Shows the success message.
   */
  showSuccessMessage() {
    this.successElement.classList.remove('hidden');
  }
}

if (!customElements.get('product-stock-notification')) {
  window.ProductStockNotification = ProductStockNotification;
  customElements.define('product-stock-notification', ProductStockNotification);
}
